import { DatePicker, Form, InputNumber, Select } from 'antd'
import dayjs from 'dayjs'
import { serviceTermSchema, type ServiceChargeInput, type ServiceTermInput } from '@super-mega/server/contracts/order'
import { RANGE_PRESETS } from '@/const/range-presets.ts'
import { SERVICE_PRICING_UNIT_OPTIONS } from './const/service-order-pricing.ts'
import type { ServiceOrderCreateFormValues } from './service-order-form.types.ts'

export function ServiceOrderCreatePricingFields({ name, disabled }: { name: number; disabled: boolean }) {
  const form = Form.useFormInstance<ServiceOrderCreateFormValues>()
  const items = Form.useWatch('items', { form, preserve: true }) as ServiceOrderCreateFormValues['items'] | undefined
  const item = items?.at(name)
  if (!item?.businessId) return null

  if (!item.term) {
    return (
      <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
        <Form.Item
          label="应收金额"
          name={[name, 'agreedAmount']}
          rules={[{ required: true, type: 'number', min: 0, message: '请输入应收金额' }]}
        >
          <InputNumber disabled={disabled} className="w-full!" min={0} precision={2} prefix="¥" />
        </Form.Item>
        <Form.Item label="成本（非必填）" name={[name, 'costAmount']} preserve={false}>
          <InputNumber disabled={disabled} className="w-full!" min={0} precision={2} prefix="¥" placeholder="选填" />
        </Form.Item>
      </div>
    )
  }

  return (
    <Form.Item
      name={[name, 'term']}
      rules={[
        {
          validator: async (_, value) => {
            const parsed = serviceTermSchema.safeParse(value)
            if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? '请完善服务期限与金额')
            if (parsed.data.rules.length !== 1) throw new Error('每项服务只配置一项费用')
          }
        }
      ]}
    >
      <ServiceOrderCreateTermEditor disabled={disabled} />
    </Form.Item>
  )
}

function ServiceOrderCreateTermEditor({
  value,
  onChange,
  disabled
}: {
  value?: ServiceTermInput
  onChange?: (value: ServiceTermInput) => void
  disabled: boolean
}) {
  if (!value) return null
  const rule = value.rules[0]
  const updateRule = (patch: Partial<ServiceChargeInput>) => onChange?.({ ...value, rules: [{ ...rule, ...patch }] })

  return (
    <div className="grid grid-cols-1 gap-x-4 lg:grid-cols-2">
      <Form.Item label="服务期限（含结束当天）" required>
        <DatePicker.RangePicker
          presets={RANGE_PRESETS}
          className="w-full"
          disabled={disabled}
          allowClear={false}
          format="YYYY-MM-DD"
          placeholder={['开始服务日', '最后服务日']}
          value={[dayjs(value.startAt), dayjs(value.endAt)]}
          onChange={(dates) => {
            if (dates?.[0] && dates[1]) {
              onChange?.({ ...value, startAt: dates[0].format('YYYY-MM-DD'), endAt: dates[1].format('YYYY-MM-DD') })
            }
          }}
        />
      </Form.Item>
      <Form.Item label="服务单价" required>
        <InputNumber
          aria-label="服务单价"
          className="w-full!"
          disabled={disabled}
          min={0}
          max={9_999_999_999.99}
          precision={2}
          prefix="¥"
          value={rule.unitPrice}
          onChange={(price) => updateRule({ unitPrice: price ?? 0 })}
        />
      </Form.Item>
      <Form.Item label="计价单位">
        <Select
          aria-label="计价单位"
          disabled={disabled}
          value={rule.pricingUnit}
          options={SERVICE_PRICING_UNIT_OPTIONS}
          onChange={(pricingUnit) =>
            updateRule({ pricingUnit, cycleUnit: pricingUnit, cycleInterval: 1, billingDay: 1 })
          }
        />
      </Form.Item>
      {rule.pricingUnit === 'month' && (
        <Form.Item label="每月出账日" extra="短月取月末；首期从服务开始日计费。">
          <InputNumber
            aria-label="每月出账日"
            className="w-full!"
            disabled={disabled}
            min={1}
            max={31}
            precision={0}
            suffix="号"
            value={rule.billingDay ?? 1}
            onChange={(billingDay) => updateRule({ billingDay: billingDay ?? 1 })}
          />
        </Form.Item>
      )}
      <p className="text-text-secondary col-span-full text-sm">
        {rule.pricingUnit === 'day' ? '按日计价，每天出账。' : '按月计价，每月出账；首尾不足整月按实际天数折算。'}
      </p>
    </div>
  )
}
